// Mirrors the backend enums in Capeesh.SharedLogic/Models/Admin/CourseGroupStats.
// The API serialises enums as numbers (Newtonsoft with camelCase properties, no string enum
// converter), so these are numeric on purpose - do not switch them to strings without changing
// the backend serialiser too.

export const CourseStatFilterScope = {
    CourseGroup: 0,
    Topic: 1,
}

export const CourseStatMetric = {
    AverageScoreNormalized: 0,
    MinScoreNormalized: 1,
    MaxScoreNormalized: 2,
    AverageStars: 10,
    LessonsPassedCount: 20,
    LessonsPassedFraction: 21,
    LessonsPlayedCount: 22,
    IsFinished: 30,
    AllLessonsPassed: 31,
    CompletionFraction: 32,
    HasStarted: 33,
    TimeSpentMinutes: 40,
    DaysSinceLastActivity: 50,
}

export const CourseStatOperator = {
    LessThan: 0,
    LessThanOrEqual: 1,
    GreaterThan: 2,
    GreaterThanOrEqual: 3,
    Equal: 4,
    NotEqual: 5,
    Between: 6,
    IsTrue: 7,
    IsFalse: 8,
}

export const CourseStatFilterCombinator = {
    And: 0,
    Or: 1,
}

export const CourseStatResultGranularity = {
    UserPerTopic: 0,
    UserPerCourseGroup: 1,
    UserOnly: 2,
}

export const CourseStatFilterParameterType = {
    NormalizedPercentage: 0,
    Days: 1,
    Count: 2,
}

// Mirrors UserStatisticsVisibility on the backend (School.cs). Only SchoolMode permits statistics
// about named individuals; the other two are workplace customers where a named list of learners
// with their scores is a surveillance concern rather than a teaching tool.
//
// Note the enum comment on MediumStrict in the backend says it hides individual statistics, but the
// admin UI labels it "Normal Mode" and the behaviour follows the label. The comment is stale.
export const organizationAllowsIndividualUserStatistics = (organizationDto) =>
    organizationDto?.userStatisticsVisibility === 1 // UserStatisticsVisibility.SchoolMode

export const CourseStatFilterPreset = {
    LowScoreButCompleted: 0,
    NeverStarted: 1,
    StartedButStalled: 2,
    CompletedEverything: 3,
    StrugglingInCourse: 4,
    HighPerformers: 5,
}

// How a metric's value should be entered and displayed. Booleans take no value at all, which is
// why the operator list is metric dependent rather than global.
export const CourseStatMetricValueType = {
    NormalizedPercentage: 'normalizedPercentage',
    Count: 'count',
    Stars: 'stars',
    Minutes: 'minutes',
    Days: 'days',
    Boolean: 'boolean',
}

export const courseStatMetricValueTypes = {
    [CourseStatMetric.AverageScoreNormalized]: CourseStatMetricValueType.NormalizedPercentage,
    [CourseStatMetric.MinScoreNormalized]: CourseStatMetricValueType.NormalizedPercentage,
    [CourseStatMetric.MaxScoreNormalized]: CourseStatMetricValueType.NormalizedPercentage,
    [CourseStatMetric.LessonsPassedFraction]: CourseStatMetricValueType.NormalizedPercentage,
    [CourseStatMetric.CompletionFraction]: CourseStatMetricValueType.NormalizedPercentage,
    [CourseStatMetric.AverageStars]: CourseStatMetricValueType.Stars,
    [CourseStatMetric.LessonsPassedCount]: CourseStatMetricValueType.Count,
    [CourseStatMetric.LessonsPlayedCount]: CourseStatMetricValueType.Count,
    [CourseStatMetric.TimeSpentMinutes]: CourseStatMetricValueType.Minutes,
    [CourseStatMetric.DaysSinceLastActivity]: CourseStatMetricValueType.Days,
    [CourseStatMetric.IsFinished]: CourseStatMetricValueType.Boolean,
    [CourseStatMetric.AllLessonsPassed]: CourseStatMetricValueType.Boolean,
    [CourseStatMetric.HasStarted]: CourseStatMetricValueType.Boolean,
}

export const booleanOperators = [CourseStatOperator.IsTrue, CourseStatOperator.IsFalse]

export const numericOperators = [
    CourseStatOperator.LessThan,
    CourseStatOperator.LessThanOrEqual,
    CourseStatOperator.GreaterThan,
    CourseStatOperator.GreaterThanOrEqual,
    CourseStatOperator.Equal,
    CourseStatOperator.NotEqual,
    CourseStatOperator.Between,
]

export const isBooleanMetric = (metric) =>
    courseStatMetricValueTypes[metric] === CourseStatMetricValueType.Boolean

export const operatorsForMetric = (metric) => (isBooleanMetric(metric) ? booleanOperators : numericOperators)

export const operatorTakesValue = (operatorValue) =>
    operatorValue !== CourseStatOperator.IsTrue && operatorValue !== CourseStatOperator.IsFalse

export const operatorTakesSecondValue = (operatorValue) => operatorValue === CourseStatOperator.Between

// Mirrors CourseStatResultSortColumn on the backend. The same value is sent with an export so the
// downloaded file comes out in the order shown on screen.
export const CourseStatResultSortColumn = {
    AverageScore: 0,
    User: 1,
    Email: 2,
    Classroom: 3,
    Topic: 4,
    Progress: 5,
    LastActivity: 6,
}

const firstClassroom = (entry) => {
    if (!entry.classrooms || entry.classrooms.length === 0) {
        return null
    }
    return [...entry.classrooms].sort((a, b) => a.localeCompare(b))[0]
}

const progressOf = (entry) => (entry.lessonsInCourse > 0 ? entry.lessonsPassed / entry.lessonsInCourse : null)

const displayNameOf = (entry) => entry.dashboardNickName || entry.name || entry.email

// Missing values sort last whichever way the column points, and blank counts as missing.
const compareStrings = (left, right) => {
    const leftMissing = !left || left.trim() === ''
    const rightMissing = !right || right.trim() === ''
    if (leftMissing && rightMissing) return 0
    if (leftMissing) return 1
    if (rightMissing) return -1
    return left.localeCompare(right)
}

const compareNullableNumbers = (left, right, direction) => {
    const leftMissing = left === null || left === undefined
    const rightMissing = right === null || right === undefined
    if (leftMissing && rightMissing) return 0
    // Applied before the direction multiplier, so blanks stay at the bottom when reversed.
    if (leftMissing) return 1
    if (rightMissing) return -1
    return direction * (left < right ? -1 : left > right ? 1 : 0)
}

/**
 * Orders filter result entries for display.
 *
 * This has a twin: CourseStatFilterResultSorter on the backend, which orders the same rows for the
 * Excel export. They have to agree or a downloaded file will not match the screen it came from.
 * Three rules, both sides:
 *
 *   1. Missing values sort last in BOTH directions. No score is not a score of zero.
 *   2. Strings compare with localeCompare, so Norwegian letters land where a reader expects.
 *   3. Ties break on email, never reversed, so repeated exports are identical.
 */
export const compareCourseStatResultEntries = (left, right, sortBy, sortDescending) => {
    const direction = sortDescending ? -1 : 1

    let comparison = 0
    switch (sortBy) {
        case CourseStatResultSortColumn.User:
            comparison = direction * compareStrings(displayNameOf(left), displayNameOf(right))
            break
        case CourseStatResultSortColumn.Email:
            comparison = direction * compareStrings(left.email, right.email)
            break
        case CourseStatResultSortColumn.Classroom:
            comparison = direction * compareStrings(firstClassroom(left), firstClassroom(right))
            break
        case CourseStatResultSortColumn.Topic:
            comparison = direction * compareStrings(left.topicName, right.topicName)
            break
        case CourseStatResultSortColumn.Progress:
            comparison = compareNullableNumbers(progressOf(left), progressOf(right), direction)
            break
        case CourseStatResultSortColumn.LastActivity:
            comparison = compareNullableNumbers(
                left.lastActivityAt ? new Date(left.lastActivityAt).getTime() : null,
                right.lastActivityAt ? new Date(right.lastActivityAt).getTime() : null,
                direction
            )
            break
        default:
            comparison = compareNullableNumbers(
                left.averageScoreNormalized,
                right.averageScoreNormalized,
                direction
            )
            break
    }

    if (comparison !== 0) {
        return comparison
    }

    return compareStrings(left.email, right.email)
}

export const CourseStatsSortPreset = {
    MostUsed: 'mostUsed',
    LowestAdoption: 'lowestAdoption',
    LowestAverageScore: 'lowestAverageScore',
}

// Formatting helpers. Scores are 0..1 on the wire and percentages on screen; keeping the
// conversion in one place avoids the classic "60 vs 0.6" threshold bug.
export const formatNormalizedAsPercent = (value, emptyLabel = '-') => {
    if (value === null || value === undefined) {
        return emptyLabel
    }
    return `${Math.round(value * 100)}%`
}

export const formatFraction = (value, total, emptyLabel = '-') => {
    if (value === null || value === undefined) {
        return emptyLabel
    }
    return `${Math.round(value * 100)}% · ${total}`
}

export const formatHours = (value) => {
    if (!value) {
        return '0'
    }
    return value >= 10 ? Math.round(value).toString() : value.toFixed(1)
}

export const formatStars = (value, emptyLabel = '-') => {
    if (value === null || value === undefined) {
        return emptyLabel
    }
    return value.toFixed(1)
}

export default {
    CourseStatFilterScope,
    CourseStatMetric,
    CourseStatOperator,
    CourseStatFilterCombinator,
    CourseStatResultGranularity,
    CourseStatFilterParameterType,
}
